import { CaseId, TDOId } from '@cbsa/state/modules/universal';
import { ROUTE_MEDIA_DETAILS } from '@common-modules/routing';
import {
  MainPageStore,
  StructuredDataObject,
  SummaryTDO,
  SummaryTDOJob,
} from '../models';
import {
  DeleteSDOBulkFailureResponse,
  DeleteSDOBulkResponse,
  DeleteTDOBulkFailureResponse,
  DeleteTDOBulkResponse,
  FetchOrganizationJobsResponse,
  FetchOrganizationSDOsByTDOsIdResponse,
  FetchOrganizationTDOByIdResponse,
  FetchOrganizationTDOsResponse,
  FetchOrganizationTasksResponse,
} from '../services';
import {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import { createAction } from '@reduxjs/toolkit';

// helpers.callGraphQLApi() is non-idiomatic. NOOP action to comply.
export const NOOP_ORGANIZATION_ACTION = 'organization/noop';

export const UPSERT_ORGANIZATION_SDO = createAction<{
  id?: string;
  data: {
    tdoId: TDOId;
    statusLabel?: string;
    statusKey?: string;
    isCustomStatus?: boolean;
  };
}>('organization/upsert-sdo');
export const UPSERT_ORGANIZATION_SDO_SUCCESS = createGraphQLSuccessAction<{
  createStructuredData: StructuredDataObject;
}>('organization/upsert-sdo-successfully');
export const UPSERT_ORGANIZATION_SDO_FAILURE = createGraphQLFailureAction(
  'organization/upsert-sdo-failed'
);

export const DELETE_ORGANIZATION_SDO = createAction<{
  id?: string;
  tdoId: string;
}>('organization/delete-sdo');
export const DELETE_ORGANIZATION_SDO_SUCCESS = createGraphQLSuccessAction<
  {
    deleteStructuredData: { id: string };
  },
  {
    schemaId: string;
    id: string;
    tdoId: TDOId;
  },
  true
>('organization/delete-sdo-successfully');
export const DELETE_ORGANIZATION_SDO_FAILURE = createGraphQLFailureAction(
  'organization/delete-sdo-failed'
);
export const DELETE_ORGANIZATION_SDO_SUCCESS_UPDATE_STORE = createAction<{
  tdoIds: TDOId[];
}>('organization/delete-sdo-successfully-update-store');

export const UNLINK_SELECTED_TDOS = createAction(
  'organization/unlink-selected-tdos'
);
export const DELETE_SDO_BULK_SUCCESS =
  createGraphQLSuccessAction<DeleteSDOBulkResponse>(
    'organization/delete-sdo-bulk-successfully'
  );
export const DELETE_SDO_BULK_FAILURE =
  createGraphQLFailureAction<DeleteSDOBulkFailureResponse>(
    'organization/delete-sdo-bulk-failed'
  );

export const SOFT_DELETE_SDO_BULK_SUCCESS =
  createGraphQLSuccessAction<DeleteSDOBulkResponse>(
    'organization/soft-delete-sdo-bulk-successfully'
  );
export const SOFT_DELETE_SDO_BULK_FAILURE = createGraphQLFailureAction(
  'organization/soft-delete-sdo-bulk-failed'
);

export const DELETE_SELECTED_TDOS = createAction(
  'organization/delete-selected-tdos'
);
export const DELETE_TDO_BULK_SUCCESS =
  createGraphQLSuccessAction<DeleteTDOBulkResponse>(
    'organization/delete-tdo-bulk-successfully'
  );
export const DELETE_TDO_BULK_FAILURE =
  createGraphQLFailureAction<DeleteTDOBulkFailureResponse>(
    'organization/delete-tdo-bulk-failed'
  );

export const UPDATE_STORE_SOFT_DELETED_TDOS = createAction<{
  tdoIds: TDOId[];
}>('organization/update-store-soft-deleted_tdos');

export const SOFT_DELETE_SELECTED_TDOS = createAction(
  'organization/soft-delete-selected-tdos'
);
export const SOFT_DELETE_TDO_BULK_SUCCESS =
  createGraphQLSuccessAction<DeleteTDOBulkResponse>(
    'organization/soft-delete-tdo-bulk-successfully'
  );
export const SOFT_DELETE_TDO_BULK_FAILURE =
  createGraphQLFailureAction<DeleteTDOBulkFailureResponse>(
    'organization/soft-delete-tdo-bulk-failed'
  );

export const FETCH_ORGANIZATION_SDOS = createAction<{
  limit: number;
  offset: number;
}>('organization/fetch-sdos');
export const FETCH_ORGANIZATION_SDOS_SUCCESS = createGraphQLSuccessAction<
  {
    structuredDataObjects: {
      records: {
        id: string;
        data: any;
        createdDateTime: string;
        modifiedDateTime: string;
      }[];
    };
  },
  {
    schemaId: string;
    limit: number;
    offset: number;
    filter: Record<string, string> | null;
  },
  true
>('organization/fetch-sdos-successfully');
export const FETCH_ORGANIZATION_SDOS_FAILURE = createGraphQLFailureAction(
  'organization/fetch-sdos-failed'
);

export const FETCH_ORGANIZATION_SDOS_BY_ID = createAction<{ tdoId: TDOId }>(
  'organization/fetch-sdos-by-tdo-id'
);
export const FETCH_ORGANIZATION_SDOS_BY_ID_SUCCESS =
  createGraphQLSuccessAction<FetchOrganizationSDOsByTDOsIdResponse>(
    'organization/fetch-sdos-by-tdo-id-successfully'
  );
export const FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE = createGraphQLFailureAction(
  'organization/fetch-sdos-by-tdo-id-failed'
);

export const FETCH_ORGANIZATION_TDOS = createAction<{
  sdos: { tdoId: TDOId }[];
}>('organization/fetch-tdos');
export const FETCH_ORGANIZATION_TDOS_SUCCESS =
  createGraphQLSuccessAction<FetchOrganizationTDOsResponse>(
    'organization/fetch-tdos-successfully'
  );

export const FETCH_ORGANIZATION_TDOS_FAILURE = createGraphQLFailureAction<
  {
    readonly name: string;
    readonly data: { readonly objectId: string };
  }[]
>('organization/fetch-tdos-failed');

export const FETCH_ORGANIZATION_TDO_BY_ID = createAction<{ tdoId: TDOId }>(
  'organization/fetch-tdo'
);
export const FETCH_ORGANIZATION_TDO_BY_ID_SUCCESS =
  createGraphQLSuccessAction<FetchOrganizationTDOByIdResponse>(
    'organization/fetch-tdo-successfully'
  );
export const FETCH_ORGANIZATION_TDO_BY_ID_FAILURE = createGraphQLFailureAction(
  'organization/fetch-tdo-failed'
);

export const FETCH_NEXT_BLOCK = createAction('organization/fetch-next-block');
export const UPDATE_PAGINATION = createAction<{
  currentPage?: number;
  itemsPerPage?: number;
  hasMore?: boolean;
  loadingNextPage?: boolean;
  invalidSDOCount?: number;
  sdoOffset?: number;
}>('organization/update-pagination');

export const CHANGE_FILTER = createAction<{ filter: MainPageStore['filter'] }>(
  'organization/change-filter'
);

export const CLEAR_ALL = createAction('organization/clear-all');

// VE-26729: mark / clear the "Home is showing stale data" flag. See MainPageStore.
export const MARK_HOME_LIST_STALE = createAction(
  'organization/mark-home-list-stale'
);
export const CLEAR_HOME_LIST_STALE = createAction(
  'organization/clear-home-list-stale'
);

export const TOGGLE_SELECT_TDO = createAction<{ tdoId: TDOId }>(
  'organization/toggle-select-tdo'
);
export const TOGGLE_SELECT_ALL_TDOS = createAction<{
  filteredTDOs: SummaryTDO[];
}>('organization/toggle-select-all-tdos');
export const CLEAR_SELECTED_TDOS = createAction(
  'organization/clear-selected-tdos'
);
export const CLEAR_SELECTED_TDOS_AND_TAG_DELETING = createAction(
  'organization/clear-selected-tdos-and-remove-selected-tdos'
);

export const FETCH_ORGANIZATION_TASKS_SUCCESS =
  createGraphQLSuccessAction<FetchOrganizationTasksResponse>(
    'organization/fetch-organization-tasks-success'
  );
export const FETCH_ORGANIZATION_TASKS_FAILURE = createGraphQLFailureAction(
  'organization/fetch-organization-tasks-failure'
);
export const UPDATE_ORG_TDOS_TASKS = createAction<{
  tasks: FetchOrganizationTasksResponse['tasks']['records'];
}>('organization/update-org-tdos-tasks');

export const FETCH_ORGANIZATION_JOBS_SUCCESS =
  createGraphQLSuccessAction<FetchOrganizationJobsResponse>(
    'organization/fetch-organization-jobs-success'
  );
export const FETCH_ORGANIZATION_JOBS_FAILURE = createGraphQLFailureAction(
  'organization/fetch-organization-jobs-failure'
);
export const UPDATE_ORG_TDOS_JOBS = createAction<{
  jobs: SummaryTDOJob[];
}>('organization/update-org-tdos-jobs');

// Mirror the `recorded` the PDF fast path has already written
// server-side into `tdos[id].status`. The same field a fetch writes, so a fetch
// corrects it — unlike the `recordedTdoIds` override this replaced.
export const MARK_ORG_TDO_RECORDED = createAction<{ tdoId: string }>(
  'organization/mark-org-tdo-recorded'
);

// VE-23391: flag a TDO as newly added this session (any upload) so the grid
// shows a "New" pill on its tile.
export const MARK_ORG_TDO_NEW = createAction<{ tdoId: string }>(
  'organization/mark-org-tdo-new'
);

// VE-23391: clear a TDO's "New" pill once the user has opened the file.
export const MARK_ORG_TDO_SEEN = createAction<{ tdoId: string }>(
  'organization/mark-org-tdo-seen'
);

// VE-24901: record the TDO the user most recently opened so the grid flags its
// tile with a "Last Opened" pill (mirrors the folder view).
export const MARK_ORG_TDO_LAST_OPENED = createAction<{ tdoId: string }>(
  'organization/mark-org-tdo-last-opened'
);

export const updateOrgTdosTasksAction = (
  tasks: FetchOrganizationTasksResponse['tasks']['records']
) => UPDATE_ORG_TDOS_TASKS({ tasks });

export const actionMarkOrgTdoRecorded = (tdoId: string) =>
  MARK_ORG_TDO_RECORDED({ tdoId });

export const actionMarkOrgTdoNew = (tdoId: string) =>
  MARK_ORG_TDO_NEW({ tdoId });

export const actionMarkOrgTdoSeen = (tdoId: string) =>
  MARK_ORG_TDO_SEEN({ tdoId });

export const actionMarkOrgTdoLastOpened = (tdoId: string) =>
  MARK_ORG_TDO_LAST_OPENED({ tdoId });

export const updateOrgTdosJobsAction = (jobs: SummaryTDOJob[]) =>
  UPDATE_ORG_TDOS_JOBS({ jobs });

// /**
//  * @const (todoId: string) => Action
//  */
export const openMediaDetails = ({
  caseId,
  tdoId,
}: {
  caseId?: CaseId;
  tdoId: TDOId;
}) => ROUTE_MEDIA_DETAILS({ caseId, tdoId });

/**
 * Insert or update an SDO. Leave id undefined for insert.
 */
export const actionUpsertSDO = (payload: {
  id?: string;
  data: {
    tdoId: TDOId;
    statusLabel?: string;
    statusKey?: string;
    isCustomStatus?: boolean;
  };
}) => UPSERT_ORGANIZATION_SDO(payload);

/**
 * Fetch a block of SDOs.
 */
export const actionFetchOrgSDOs = (payload: {
  limit: number;
  offset: number;
}) => FETCH_ORGANIZATION_SDOS(payload);

/**
 * Fetch a block of SDOs.
 */
export const actionFetchOrgSDOsByTDOsId = (payload: { tdoId: TDOId }) =>
  FETCH_ORGANIZATION_SDOS_BY_ID(payload);

/**
 * Fetch TDOs by SDO map.
 */
export const actionFetchOrgTDOs = (payload: { sdos: { tdoId: TDOId }[] }) =>
  FETCH_ORGANIZATION_TDOS(payload);

/**
 * Fetch TDOs Success.
 */
export const actionFetchOrgTDOsSuccess = (payload: {
  [id: string]: SummaryTDO;
}) => FETCH_ORGANIZATION_TDOS_SUCCESS(payload, undefined);

// /**
//  * Fetch TDO by Id.
//  * @param {{ tdoId: TDOId }} payload
//  */
export const actionFetchOrgTDODetail = (payload: { tdoId: TDOId }) =>
  FETCH_ORGANIZATION_TDO_BY_ID(payload);

// /**
//  * Remove an SDO.
//  * @param payload - SDO Id or TDO Id required.
//  */
export const actionDeleteOrgSDO = (payload: { id?: string; tdoId: string }) =>
  DELETE_ORGANIZATION_SDO(payload);

// /**
//  * Unlink by deleting SDOs associated with TDOs.
//  * @param payload - TDO Ids required.
//  */
export const actionUnlinkSelectedTDOs = () => UNLINK_SELECTED_TDOS();

// /**
//  * Remove multiple TDOs with linked SDOs.
//  * @param payload - TDO Ids required.
//  */
export const actionDeleteSelectedTDOs = () => DELETE_SELECTED_TDOS();

export const actionSoftDeleteSelectedTDOs = () => SOFT_DELETE_SELECTED_TDOS();

/**
 * Attempt to pull the next block of TDOs.
 */
export const actionFetchNextBlock = () => FETCH_NEXT_BLOCK();

export const actionUpdatePagination = (payload: {
  currentPage?: number;
  itemsPerPage?: number;
  hasMore?: boolean;
  loadingNextPage?: boolean;
  invalidSDOCount?: number;
  sdoOffset?: number;
}) => UPDATE_PAGINATION(payload);

export const actionClearAll = () => CLEAR_ALL();

export const actionMarkHomeListStale = () => MARK_HOME_LIST_STALE();

export const actionClearHomeListStale = () => CLEAR_HOME_LIST_STALE();

export const actionToggleTDO = (payload: { tdoId: TDOId }) =>
  TOGGLE_SELECT_TDO(payload);

export const actionToggleAllTDOs = (payload: { filteredTDOs: SummaryTDO[] }) =>
  TOGGLE_SELECT_ALL_TDOS(payload);

export const actionClearSelectedTDOs = () => CLEAR_SELECTED_TDOS();

export const actionClearSelectedTDOsAndTagDeleting = () =>
  CLEAR_SELECTED_TDOS_AND_TAG_DELETING();

export const actionUpdateStoreSoftDeletedTdos = (payload: {
  tdoIds: TDOId[];
}) => UPDATE_STORE_SOFT_DELETED_TDOS(payload);
